import type { BX24Result, CrmDeal, PlacementInfo, DiskUploadedFile, DiskFolderChild, DiskAttachedObject } from './types';

export function initBX24(): Promise<void> {
  return new Promise((resolve) => {
    window.BX24.init(resolve);
  });
}

export function getPlacementInfo(): Promise<PlacementInfo> {
  return Promise.resolve(window.BX24.placement.info());
}

function callMethod<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  return new Promise((resolve, reject) => {
    window.BX24.callMethod(method, params, (result: BX24Result<T>) => {
      const err = result.error();
      if (err) {
        reject(new Error(`BX24 ${method}: ${err.description} (${err.code})`));
      } else {
        resolve(result.data());
      }
    });
  });
}

export function getDeal(id: string): Promise<CrmDeal> {
  return callMethod<CrmDeal>('crm.deal.get', { id });
}

/** Fetch deal including custom UF_ fields. CrmDeal is typed as Record for UF fields. */
export function getDealRaw(id: string): Promise<Record<string, unknown>> {
  return callMethod<Record<string, unknown>>('crm.deal.get', { id });
}

/**
 * Post a timeline comment on the deal with attached files.
 * FILES format: [['filename.xlsx', 'base64string'], ...]
 * Returns the new comment ID.
 */
export function uploadTimelineComment(
  dealId: string,
  comment: string,
  files: Array<{ name: string; base64: string }>,
): Promise<number> {
  return callMethod<number>('crm.timeline.comment.add', {
    fields: {
      ENTITY_ID: parseInt(dealId, 10),
      ENTITY_TYPE: 'deal',
      COMMENT: comment,
      FILES: files.map((f) => [f.name, f.base64]),
    },
  });
}

/**
 * List direct children (files + subfolders) of a Bitrix24 Disk folder.
 */
export function getDiskFolderChildren(folderId: number): Promise<DiskFolderChild[]> {
  return callMethod<DiskFolderChild[]>('disk.folder.getchildren', { id: folderId });
}

/**
 * Given a parent folder ID, find the subfolder whose name starts with the given prefix
 * (e.g. "3." to find "3. Расчет"). Returns null if not found.
 * Uses absence of DOWNLOAD_URL to identify folders (files always have it).
 */
export async function findSubfolderByPrefix(
  parentFolderId: number,
  namePrefix: string,
): Promise<number | null> {
  const children = await getDiskFolderChildren(parentFolderId);
  const match = children.find(
    (c) => c.DOWNLOAD_URL === undefined && c.NAME.startsWith(namePrefix),
  );
  return match?.ID ?? null;
}

/**
 * Upload a new file to a Bitrix24 Disk folder.
 */
export function uploadFileToDiskFolder(
  folderId: number,
  name: string,
  base64: string,
): Promise<DiskUploadedFile> {
  return callMethod<DiskUploadedFile>('disk.folder.uploadfile', {
    id: folderId,
    data: { NAME: name },
    fileContent: [name, base64],
    generateUniqueName: false,
  });
}

/**
 * Upload a new version of an existing Bitrix24 Disk file.
 * The file ID stays the same; only its content changes.
 */
export function uploadFileVersion(
  fileId: number,
  name: string,
  base64: string,
): Promise<DiskUploadedFile> {
  return callMethod<DiskUploadedFile>('disk.file.uploadversion', {
    id: fileId,
    data: { NAME: name },
    fileContent: [name, base64],
  });
}

/**
 * Upload-or-update a file in a folder by name.
 * If a file with the same name already exists, uploads a new version (preserves file ID).
 * Otherwise, creates a new file.
 */
export async function upsertFileToDiskFolder(
  folderId: number,
  name: string,
  base64: string,
): Promise<DiskUploadedFile> {
  const children = await getDiskFolderChildren(folderId);
  const existing = children.find(
    (c) => c.DOWNLOAD_URL !== undefined && c.NAME === name,
  );
  if (existing) {
    return uploadFileVersion(existing.ID, name, base64);
  }
  return uploadFileToDiskFolder(folderId, name, base64);
}

/**
 * Get metadata for a Bitrix Disk file by its numeric ID.
 * Returns DOWNLOAD_URL among other fields.
 */
export function getDiskFile(id: number): Promise<DiskUploadedFile> {
  return callMethod<DiskUploadedFile>('disk.file.get', { id });
}

/**
 * Get a disk attached object by its ID.
 * "Файл" UF fields on CRM entities store file references as attached object IDs.
 * The returned DOWNLOAD_URL (uf.php?...) includes an auth token and can be fetched cross-origin.
 */
export function getAttachedObject(id: number): Promise<DiskAttachedObject> {
  return callMethod<DiskAttachedObject>('disk.attachedObject.get', { id });
}

/**
 * Fetch the file bytes for a Bitrix file by its download URL.
 *
 * Handles:
 *  - Relative URLs → resolved against portal domain (BX24.getAuth().domain)
 *  - show_file.php URLs → routed through /bx24-proxy to avoid CORS
 *    (the endpoint has no Access-Control-Allow-Origin header;
 *     our own server proxies the request transparently)
 *  - Other absolute URLs (disk_file DOWNLOAD_URL with embedded auth token) → fetched directly
 */
export async function fetchDiskFile(downloadUrl: string): Promise<ArrayBuffer> {
  let url = downloadUrl;

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    const { domain } = window.BX24.getAuth();
    url = `https://${domain}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  // show_file.php has no CORS headers — route through /bx24-proxy on our own server.
  // In dev: Vite proxy (configured via VITE_BX_PORTAL in vite.config.ts).
  // In prod: nginx proxy (location /bx24-proxy/ { proxy_pass <portal>; }).
  if (url.includes('show_file.php')) {
    const parsed = new URL(url);
    url = '/bx24-proxy' + parsed.pathname + parsed.search;
  }

  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) throw new Error(`Ошибка загрузки файла: ${resp.status} ${resp.statusText}`);
  return resp.arrayBuffer();
}
